import {ArrowRight} from 'lucide-react'
import {useI18n} from '@/i18n'
import {Badge} from '@/components/ui/badge'
import {Skeleton} from '@/components/ui/skeleton'
import {InlineRow} from '@/components/shared/InlineRow'
import {MutedText} from '@/components/shared/MutedText'
import {Icon} from '@/components/shared/Icon'
import {formatTimestamp, formatRelativeTime} from '@/lib/date-utils'
import type {IdentityLifecycleEvent} from '@/types/identity'
import {IdentityLifecycleStateBadge} from '@/types/identity'
import {TriggerSourceBadge} from '@/types/trigger-source-badge'

const REASON_TRUNCATE_LIMIT = 80

/**
 * Above-the-fold inline summary of the identity's most recent lifecycle event
 * (spec §8.1.1). Renders the state-pair (or "Created as X" for the initial row),
 * trigger-source badge, actor pill, relative timestamp, and truncated reason.
 *
 * <p>Loading: single-line skeleton (does not block the rest of the header card).
 * Error / unavailable: muted "Lifecycle history unavailable" text — never an
 * ErrorBanner; the header card stays usable when the secondary fetch fails.
 *
 * <p>Callers pass {@code onViewTimeline} so the header stays presentational —
 * the IdentityDetailPage owns the tab-switch side-effect.
 */
export interface IdentityLifecycleHeaderProps {
    event: IdentityLifecycleEvent | null | undefined
    isLoading: boolean
    isError: boolean
    onViewTimeline: () => void
    'data-testid'?: string
}

export function IdentityLifecycleHeader({
    event,
    isLoading,
    isError,
    onViewTimeline,
    'data-testid': testid,
}: IdentityLifecycleHeaderProps) {
    const i18n = useI18n()

    if (isLoading) {
        return (
            <Skeleton
                className="h-6 w-3/4"
                data-testid={testid ? `${testid}-skeleton` : 'lifecycle-header-skeleton'}
            />
        )
    }

    if (isError || !event) {
        return (
            <MutedText size="sm" data-testid={testid ? `${testid}-unavailable` : 'lifecycle-header-unavailable'}>
                {i18n.lifecycleEvents.header.unavailable}
            </MutedText>
        )
    }

    const toBadge = IdentityLifecycleStateBadge.of(event.toState)
    const fromBadge = event.fromState ? IdentityLifecycleStateBadge.of(event.fromState) : null
    const triggerBadge = TriggerSourceBadge.of(event.triggerSource)
    const triggerLabel = i18n.lifecycleEvents.triggerSource[event.triggerSource]
    const actorName = event.actor?.displayName ?? i18n.lifecycleEvents.timeline.system
    const absoluteTimestamp = formatTimestamp(event.changedAt)
    const relativeTimestamp = formatRelativeTime(event.changedAt)
    const truncatedReason = truncateReason(event.reason)

    return (
        <InlineRow
            gap="xs"
            wrap
            data-testid={testid ?? 'lifecycle-header-summary'}
        >
            {fromBadge ? (
                <>
                    <Badge variant={fromBadge.variant} data-testid="lifecycle-header-from-state">
                        {event.fromState}
                    </Badge>
                    <Icon icon={ArrowRight} size="xs" muted/>
                    <Badge variant={toBadge?.variant} data-testid="lifecycle-header-to-state">
                        {event.toState}
                    </Badge>
                </>
            ) : (
                <Badge variant={toBadge?.variant} data-testid="lifecycle-header-to-state">
                    {String(i18n.lifecycleEvents.header.createdAs({state: event.toState}))}
                </Badge>
            )}
            <Badge variant={triggerBadge.variant} data-testid="lifecycle-header-trigger-source">
                {triggerLabel}
            </Badge>
            <MutedText size="sm">
                <span title={absoluteTimestamp} data-testid="lifecycle-header-timestamp">
                    {relativeTimestamp}
                </span>
                {' '}
                {i18n.lifecycleEvents.header.by}{' '}
                <span data-testid="lifecycle-header-actor">{actorName}</span>
            </MutedText>
            {truncatedReason && (
                <MutedText size="sm">
                    <span title={event.reason ?? undefined} data-testid="lifecycle-header-reason">
                        {i18n.lifecycleEvents.header.reasonLabel}: {truncatedReason}
                    </span>
                </MutedText>
            )}
            <button
                type="button"
                onClick={onViewTimeline}
                className="text-sm text-primary hover:underline"
                data-testid="lifecycle-header-view-timeline"
            >
                {i18n.lifecycleEvents.header.viewTimeline}
            </button>
        </InlineRow>
    )
}

function truncateReason(reason: string | null | undefined): string | null {
    if (!reason) return null
    if (reason.length <= REASON_TRUNCATE_LIMIT) return reason
    return reason.slice(0, REASON_TRUNCATE_LIMIT - 1) + '…'
}
